import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Banner } from '@spotoninc/nexus-react';
import { format, differenceInCalendarDays, addDays } from 'date-fns';
import { DEFAULT_PRICE_PER_LOCATION } from './useSubscriptionStatus';

// Banner shows once the trial ends within this many days.
const BANNER_WINDOW_DAYS = 30;
// After dismissal the banner reappears once inside this window.
const REAPPEAR_WINDOW_DAYS = 7;
const DISMISS_KEY_PREFIX = 'pa_trial_banner_dismissed_';

// The trial end is a calendar date, not a wall-clock instant: `trialEndsAt` is a
// UTC timestamp (ISO-8601 with offset) and date-only values like `2026-11-03`
// parse at UTC midnight. We normalize every value to its UTC calendar day and
// then reason about it in local time, so the displayed date, the day-count
// window, and the dismissal key all agree regardless of the viewer's timezone
// (a US merchant with an early-UTC or date-only `trialEndsAt` sees the actual
// end date, not the day before).
const toUtcCalendarDate = value => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  );
};

// Stable YYYY-MM-DD key for the UTC calendar day, used to scope a dismissal to
// one trial end date. Built from local getters because `toUtcCalendarDate`
// already shifted the day into local time.
const toDateKey = date =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;

// Milliseconds from `now` (epoch ms) until the next local midnight, used to
// advance the day-count phase on a wall-clock day boundary while the banner
// stays mounted.
const msUntilNextLocalMidnight = now => {
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next.getTime() - now;
};

// 'reappear' = inside the 7-day window, 'initial' = 8–30 days out.
const getPhase = daysUntilEnd =>
  daysUntilEnd <= REAPPEAR_WINDOW_DAYS ? 'reappear' : 'initial';

const readDismissedPhase = (key, endDate) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    // Only honor a dismissal recorded for this exact trial end date.
    if (stored?.endDate !== toDateKey(endDate)) return null;
    return stored.phase || null;
  } catch {
    return null;
  }
};

/**
 * Trial-expiring banner (PROF-1190). Info-style Nexus banner warning self-serve
 * merchants that their Profit Assist free trial is ending.
 *
 * Eligibility (self-serve cohort, trial state, feature flag) is handled by the
 * caller; this component owns the 30/7-day visibility window and per-merchant
 * dismissal persistence.
 *
 * Two Figma variants keyed off `showAction`:
 *  - Other pages (showAction=true): description + "Manage subscription" button,
 *    no title (node 20385:33368).
 *  - Subscription & Billing page (showAction=false): bold "ends in N days" title
 *    + description, no action since it would navigate to the current page
 *    (node 20408:14108).
 */
const TrialBanner = ({
  trialEndDate,
  chargeDate,
  pricePerLocation = DEFAULT_PRICE_PER_LOCATION,
  merchantKey = 'default',
  onManageSubscription,
  showAction = true,
}) => {
  const endDate = useMemo(
    () => toUtcCalendarDate(trialEndDate),
    [trialEndDate]
  );
  const dismissKey = `${DISMISS_KEY_PREFIX}${merchantKey}`;

  // `today` advances on each local day boundary so the day count and phase
  // follow wall-clock time even while ReskinnedDashboard stays mounted across
  // nested navigation (an undismissed banner ages out, a banner dismissed
  // 8–30 days out reappears at 7 days).
  const [today, setToday] = useState(() => Date.now());

  useEffect(() => {
    const timer = setTimeout(
      () => setToday(Date.now()),
      msUntilNextLocalMidnight(Date.now())
    );
    return () => clearTimeout(timer);
  }, [today]);

  const daysUntilEnd = useMemo(
    () => (endDate ? differenceInCalendarDays(endDate, today) : null),
    [endDate, today]
  );
  const currentPhase = daysUntilEnd == null ? null : getPhase(daysUntilEnd);

  const [dismissedPhase, setDismissedPhase] = useState(() =>
    endDate ? readDismissedPhase(dismissKey, endDate) : null
  );

  const handleDismiss = useCallback(() => {
    if (!endDate || !currentPhase) return;
    try {
      localStorage.setItem(
        dismissKey,
        JSON.stringify({
          endDate: toDateKey(endDate),
          phase: currentPhase,
        })
      );
    } catch {
      // Ignore write failures (e.g. storage disabled/full).
    }
    setDismissedPhase(currentPhase);
  }, [dismissKey, endDate, currentPhase]);

  // Only render within the 30-day pre-expiry window and before billing begins.
  if (!endDate || daysUntilEnd == null) return null;
  if (daysUntilEnd < 0 || daysUntilEnd > BANNER_WINDOW_DAYS) return null;

  // Dismissed inside the 7-day window -> stay hidden.
  // Dismissed only in the initial window -> reappear once inside 7 days.
  if (dismissedPhase === 'reappear') return null;
  if (dismissedPhase === 'initial' && currentPhase === 'initial') return null;

  const formattedEnd = format(endDate, 'EEE, MMM d');
  // Whole dollars render without a trailing `.00` (e.g. `$100`), matching the
  // Subscription & Billing page; a fractional price keeps two decimals.
  const priceValue = Number(pricePerLocation);
  const formattedPrice = `$${
    Number.isInteger(priceValue) ? priceValue : priceValue.toFixed(2)
  }`;
  // Prefer a server-supplied charge date (PROF-1192); otherwise billing starts
  // the day after the trial ends. Both are normalized to a UTC calendar date so
  // the displayed day matches `formattedEnd`.
  const chargeStart = toUtcCalendarDate(chargeDate) || addDays(endDate, 1);
  const formattedChargeStart = format(chargeStart, 'MMM d');
  const chargeSentence = `You\u2019ll be charged ${formattedPrice}/mo per location starting ${formattedChargeStart}.`;

  // The no-action (Subscription & Billing) variant carries a bold title with the
  // day count; the action variant on other pages omits it. The description
  // restates the exact date in both.
  const title = showAction
    ? undefined
    : `Your free trial ends in ${daysUntilEnd} ${
        daysUntilEnd === 1 ? 'day' : 'days'
      }`;
  const content = (
    <span>
      Your free trial ends {formattedEnd}. {chargeSentence}
    </span>
  );

  return (
    <Banner
      priority="informative"
      variant="global"
      title={title}
      content={content}
      actionLabel={showAction ? 'Manage subscription' : undefined}
      onActionClick={showAction ? onManageSubscription : undefined}
      onDismiss={handleDismiss}
      data-testid="trial-expiring-banner"
    />
  );
};

export default TrialBanner;
